import { Cancel, CloudUploadOutlined, EditOutlined } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { Progress, Upload } from "antd";
import { CustomButton } from "components/UI";
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import {
  ref as fireRef,
  getDownloadURL,
  uploadBytesResumable,
} from "firebase/storage";
import { storage } from "firebaseConfig";
import moment from "moment-timezone";
import React, { memo, useRef, useState } from "react";
import { useCurrentUser } from "store/users/selectors";
import styled from "styled-components";
import { v4 } from "uuid";
import ImageThemeModal from "../imageTheme";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ImageWithTextSelectedType = ({
  element,
  currentIndex,
  handleUpdateCell = () => null,
  selectedType,
  totalCells,
  handleEditingCell = () => null,
  readOnly = false,
  viewType = "",
  gridContainerWidth,
}) => {
  const currentUser = useCurrentUser();
  const showAlert = useAlert();
  const selectedTypeRef = useRef();
  const selectedTypeComponentWidth = selectedTypeRef.current?.offsetWidth;
  const [openImageModal, setOpenImageModal] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "ArrowRight") {
      const nextIndex = currentIndex === totalCells - 1 ? 0 : currentIndex + 1;
      handleEditingCell(nextIndex);
      setTimeout(() => {
        const nextInput = document.querySelector(`#input-cell-${nextIndex}`);
        if (nextInput) {
          nextInput.focus();
        }
      }, 0);
    } else if (e.key === "ArrowLeft") {
      const nextIndex = currentIndex === 0 ? totalCells - 1 : currentIndex - 1;
      handleEditingCell(nextIndex);
      setTimeout(() => {
        const nextInput = document.querySelector(`#input-cell-${nextIndex}`);
        if (nextInput) {
          nextInput.focus();
        }
      }, 0);
    } else if (e.key === "ArrowUp") {
      const checkRowCount = Math.floor(
        gridContainerWidth / selectedTypeComponentWidth
      );
      const rowIndex = Math.floor(currentIndex / checkRowCount);
      const columnIndex = currentIndex % checkRowCount;

      const prevRowIndex =
        rowIndex > 0
          ? rowIndex - 1
          : Math.floor((totalCells - 1) / checkRowCount);
      const nextIndex = Math.min(
        prevRowIndex * checkRowCount + columnIndex,
        totalCells - 1
      );

      handleEditingCell(nextIndex);
      setTimeout(() => {
        const nextInput = document.querySelector(`#input-cell-${nextIndex}`);
        if (nextInput) {
          nextInput.focus();
        }
      }, 0);
    } else if (e.key === "ArrowDown") {
      const checkRowCount = Math.floor(
        gridContainerWidth / selectedTypeComponentWidth
      );
      const rowIndex = Math.floor(currentIndex / checkRowCount);
      const columnIndex = currentIndex % checkRowCount;

      const nextRowIndex =
        (rowIndex + 1) % Math.ceil(totalCells / checkRowCount);
      const nextIndex = Math.min(
        nextRowIndex * checkRowCount + columnIndex,
        totalCells - 1
      );

      handleEditingCell(nextIndex);
      setTimeout(() => {
        const nextInput = document.querySelector(`#input-cell-${nextIndex}`);
        if (nextInput) {
          nextInput.focus();
        }
      }, 0);
    }
  };

  const handleInputChange = (e) => {
    handleUpdateCell(currentIndex, { value: e.target.value });
  };

  const handleDeleteImage = () => {
    handleUpdateCell(currentIndex, { imageUrl: "" });
  };

  const uploadToStorage = async (file) => {
    try {
      if (
        !file ||
        (file.size > MAX_FILE_SIZE && currentUser?.role !== "owe_admin")
      ) {
        return showAlert("The image will not exceed 10MB in size.", "error");
      }

      const location = "/picture/loteria";
      const id = `${moment().format("YYYY-MM-DD")}-${v4()}${file.name}`;
      const fileRef = fireRef(storage, `${location}/${id}`);
      const uploadTask = uploadBytesResumable(fileRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          handleUpdateCell(currentIndex, {
            percant: progress,
            isLoading: true,
          });
        },
        (error) => {
          console.error("Upload error:", error);
          handleUpdateCell(currentIndex, {
            isLoading: false,
            percant: 0,
          });
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            handleUpdateCell(currentIndex, {
              imageUrl: downloadURL,
              isLoading: false,
              percant: 0,
            });
          } catch (error) {
            Sentry.captureException(`632 Error in uploadToStorage: ${error}`);
            console.error("Failed to get download URL:", error);
          } finally {
            handleUpdateCell(currentIndex, {
              isLoading: false,
              percant: 0,
            });
          }
        }
      );
    } catch (error) {
      Sentry.captureException(`633 Error in uploadToStorage: ${error}`);
      console.error("Upload error:", error);
      // setLocalLoading(false);
      handleUpdateCell(currentIndex, {
        isLoading: false,
        percant: 0,
      });
    }
  };

  const handleFileUpload = async (file) => {
    try {
      if(readOnly){
        return;
      }
      await uploadToStorage(file);
      return false;
    } catch (error) {
      Sentry.captureException(`634 Error in handleFileUpload: ${error}`);
      console.error("Upload failed:", error);
      handleUpdateCell(currentIndex, {
        isLoading: false,
        percant: 0,
      });
      return Upload.LIST_IGNORE;
    }
  };

  const handleImageModal = (value) => {
    if(readOnly){
      return;
    }
    setOpenImageModal(value);
  }
  return (
    <>
      <ImageThemeModal
        open={openImageModal}
        setOpen={setOpenImageModal}
        uploadToStorage={uploadToStorage}
        currentIndex={currentIndex}
        handleUpdateCell={handleUpdateCell}
        />
      {(viewType === "viewMyLoteria" || viewType === "viewResultWheel") ? null :
        element?.imageUrl && (
          <CustomButton
            variant="transparent"
            className="delete-button-image"
            onClick={handleDeleteImage}
          >
            <Cancel />
          </CustomButton>
        )}

      <StyledCell
        ref={selectedTypeRef}
        selectedType={selectedType}
        backgroundColor={element.backgroundColor}
        className="word-cell contBingo"
        selected={element?.selected ? element?.selected : false}
        viewType={viewType}
      >
        <div className="cell-content">
          {element.imageUrl ? (
            <div className="image-container">
              <img className="uploaded-image" src={element.imageUrl} alt="" />

              {element?.isLoading ? (
                <div className="onchange-uploader">
                  <Progress
                    type="circle"
                    percent={element.percant}
                    width={50}
                    className="upload-progress"
                  />
                </div>
              ) : (
                <div onClick={() => handleImageModal(true)}>
                  {viewType === "viewMyLoteria" ||
                    viewType === "viewResultWheel" ? null : (
                    <div className="image-overlay">
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleFileUpload}
                        className="upload-button"
                        disabled={true}
                      >
                        <div className="click-to-upload">
                          <CloudUploadOutlined className="upload-icon" />
                          <span>Click to Upload</span>
                        </div>
                      </Upload>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div onClick={() => handleImageModal(true)} className="upload-container">
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleFileUpload}
                className="upload-area"
                disabled={true}
              >
                <div className="upload-content">
                  <div className="click-to-upload">
                    <CloudUploadOutlined className="upload-icon" />
                    <span>Click to Upload</span>
                  </div>

                  {element?.isLoading && (
                    <Progress
                      type="circle"
                      percent={element.percant}
                      width={50}
                      className="upload-progress"
                    />
                  )}
                </div>
              </Upload>
            </div>
          )}
          {(viewType === "viewMyLoteria" || viewType === "viewResultWheel") &&
            element.value === "" ? null : (
            <StyledInput
              id={`input-cell-${currentIndex}`}
              type="text"
              value={element.value || ""}
              onChange={handleInputChange}
              placeholder="Enter text"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              suffix={<EditOutlined />}
              disabled={readOnly}
            />
          )}
        </div>
      </StyledCell>
    </>
  );
};

const StyledCell = styled.div`
  min-width: ${(props) =>
    props?.viewType === "viewResultWheel" ? "150px" : ""};
  width: 100%;
  min-height: 150px;
  padding: 16px 16px 37px 16px;
  background-color: ${(props) => props.backgroundColor ? props.backgroundColor : "#333"};
  border: 3px dashed #080808;
  transition: all 0.3s;
  color: #fff;

  .cell-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .image-container {
    position: relative;
    width: 100%;
    height: 150px;
    overflow: hidden;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover .image-overlay {
      opacity: 1;
    }
  }

  .uploaded-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .onchange-uploader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
  }

  .ant-upload.ant-upload-disabled{
    cursor:pointer!important;
  }

  .upload-container {
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .upload-area {
    width: 100%;
    height: 100%;

    .ant-upload {
      width: 100%;
      height: 100%;
    }
  }

  .upload-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    // gap: 8px;
    color: #fff;
  }
  .click-to-upload {
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-direction: column;
    color: #fff;
    font-size: 12px;
  }
  .upload-icon {
    font-size: 32px;
    color: #fff;
  }

  .upload-progress {
    margin-top: 12px;
  }

  .ant-btn-ghost {
    color: #fff;
    border-color: #fff;

    &:hover {
      color: #1890ff;
      border-color: #1890ff;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const StyledInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 4px 11px;
  background: #00295c;
  border: none;
  border-radius: 4px 4px 0px 0px;
  color: #fff;
  transition: all 0.3s;
  text-align: left;
  position: absolute;
  bottom: 0;
  right: 0;
  &::placeholder {
    color: #fff;
  }

  &:hover,
  &:focus {
    border-color: #1890ff;
    outline: none;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

export default memo(ImageWithTextSelectedType);
